import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  PortalLoadingCard,
  PortalMetricCard,
  PortalStateCard,
  formatPortalDate,
  getStatusChipClass,
  usePortalTableStyles
} from './portalShared'

const useStyles = makeStyles((theme) => ({
  countRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    fontFamily: 'var(--df-font-mono)',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start'
    }
  },
  noteWrap: {
    maxWidth: 320
  },
  emptyCell: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  assignmentCell: {
    minWidth: 220
  },
  actionRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap'
  }
}))

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'never_done', label: 'Never Done' },
  { value: 'needs_completion', label: 'Needs Completion' },
  { value: 'due_soon', label: 'Due Soon' },
  { value: 'ok', label: 'OK' },
  { value: 'ignored', label: 'Ignored' }
]

const sortOptions = [
  { value: 'nextScheduledDate', label: 'Urgency / due date' },
  { value: 'scheduledTicketDate', label: 'Scheduled date' },
  { value: 'siteName', label: 'Property name' },
  { value: 'address', label: 'Address' },
  { value: 'status', label: 'Status bucket' }
]

const assignmentViewOptions = [
  { value: 'all', label: 'All visible sites' },
  { value: 'assigned', label: 'Assigned only' },
  { value: 'unassigned', label: 'Unassigned only' }
]

const SubcontractorSchedule = ({ user, notification }) => {
  const classes = useStyles()
  const shared = usePortalTableStyles()
  const isAdmin = user?.subcontractorProfile?.role === 'admin'
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [summary, setSummary] = useState({})
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState('all')
  const [sortBy, setSortBy] = useState('nextScheduledDate')
  const [sortOrder, setSortOrder] = useState('asc')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [assignmentView, setAssignmentView] = useState('all')
  const [crews, setCrews] = useState([])
  const [assigningItemId, setAssigningItemId] = useState('')
  const [rebuilding, setRebuilding] = useState(false)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(0)
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [searchInput])

  const loadCrews = useCallback(async () => {
    if (!isAdmin) return

    try {
      const response = await axios.get('/api/subcontractor/crews')
      setCrews((response.data?.items || []).filter(item => item.isActive !== false))
    } catch (requestError) {
      console.error('Failed to load subcontractor crews for schedule assignment', requestError)
    }
  }, [isAdmin])

  const loadSchedule = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const response = await axios.get('/api/subcontractor/schedule', {
        params: {
          page,
          limit: rowsPerPage,
          status,
          search,
          sortBy,
          sortOrder,
          assignmentView
        }
      })

      setItems(response.data?.items || [])
      setSummary(response.data?.summary || {})
      setTotal(response.data?.total || 0)
    } catch (requestError) {
      console.error('Failed to load subcontractor schedule', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load the subcontractor schedule right now.')
    } finally {
      setLoading(false)
    }
  }, [assignmentView, page, rowsPerPage, search, sortBy, sortOrder, status])

  useEffect(() => {
    loadSchedule()
  }, [loadSchedule])

  useEffect(() => {
    loadCrews()
  }, [loadCrews])

  const handleAssignCrew = async (item, crewId) => {
    setAssigningItemId(item._id)

    try {
      if (!crewId) {
        await axios.post('/api/subcontractor/schedule/clear-assignment', {
          scheduleItemId: item._id
        })
      } else {
        await axios.post('/api/subcontractor/schedule/assign-crew', {
          scheduleItemId: item._id,
          crewId
        })
      }

      notification?.({
        type: 'success',
        title: crewId ? 'Crew Assigned' : 'Assignment Cleared',
        message: crewId
          ? `${item.siteName || 'Site'} is now assigned to that subcontractor crew.`
          : `${item.siteName || 'Site'} is now unassigned.`
      })
      await loadSchedule()
    } catch (requestError) {
      console.error('Failed to update subcontractor schedule assignment', requestError)
      notification?.({
        type: 'danger',
        title: 'Assignment Failed',
        message: requestError?.response?.data?.message || 'Unable to update that crew assignment right now.'
      })
    } finally {
      setAssigningItemId('')
    }
  }

  const handleRebuildFromTerritories = async () => {
    setRebuilding(true)

    try {
      const response = await axios.post('/api/subcontractor/schedule/rebuild-from-territories', {
        overwriteManual: false
      })
      notification?.({
        type: 'success',
        title: 'Assignments Rebuilt',
        message: `Matched ${response.data?.territoryMatches || 0} schedule items from subcontractor territories.`
      })
      await loadSchedule()
    } catch (requestError) {
      console.error('Failed to rebuild subcontractor schedule assignments from territories', requestError)
      notification?.({
        type: 'danger',
        title: 'Rebuild Failed',
        message: requestError?.response?.data?.message || 'Unable to rebuild assignments from territories right now.'
      })
    } finally {
      setRebuilding(false)
    }
  }

  const summaryCards = useMemo(() => {
    const shared = [
      {
        eyebrow: 'Visible Items',
        value: summary.total || 0,
        caption: 'Total jobs on your schedule.',
        },
      {
        eyebrow: 'Overdue',
        value: summary.overdue || 0,
        caption: 'Properties already past the target date.',
        }
    ]

    if (isAdmin) {
      shared.splice(1, 0, {
        eyebrow: 'Assigned',
        value: summary.assigned || 0,
        caption: 'Jobs assigned to a crew.',
      })
      shared.push({
        eyebrow: 'Unassigned',
        value: summary.unassigned || 0,
        caption: 'Jobs not yet assigned to a crew.',
      })
      return shared
    }

    shared.push({
      eyebrow: 'Needs Completion',
      value: summary.needs_completion || 0,
      caption: 'Items that need closeout or additional work.',
    })
    shared.push({
      eyebrow: 'Due Soon',
      value: summary.due_soon || 0,
      caption: 'Upcoming work that is approaching schedule pressure.',
    })
    return shared
  }, [isAdmin, summary])

  if (loading && !items.length) {
    return <PortalLoadingCard title="Loading schedule" message="Loading your schedule..." minHeight={280} />
  }

  return (
    <div>
      <Grid container spacing={3}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.eyebrow}>
            <PortalMetricCard
              eyebrow={card.eyebrow}
              value={card.value}
              caption={card.caption}
            />
          </Grid>
        ))}
      </Grid>

      <Box mt={3}>
        <Card className={shared.controlsCard}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search properties or addresses"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={status}
                  onChange={(event) => {
                    setStatus(event.target.value)
                    setPage(0)
                  }}
                  variant="outlined"
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {isAdmin ? (
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    select
                    label="Assignment View"
                    value={assignmentView}
                    onChange={(event) => {
                      setAssignmentView(event.target.value)
                      setPage(0)
                    }}
                    variant="outlined"
                  >
                    {assignmentViewOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ) : null}
              <Grid item xs={12} sm={6} md={isAdmin ? 2 : 3}>
                <TextField
                  fullWidth
                  select
                  label="Sort by"
                  value={sortBy}
                  onChange={(event) => {
                    setSortBy(event.target.value)
                    setPage(0)
                  }}
                  variant="outlined"
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  select
                  label="Direction"
                  value={sortOrder}
                  onChange={(event) => {
                    setSortOrder(event.target.value)
                    setPage(0)
                  }}
                  variant="outlined"
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Box mt={2} display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gridGap={12}>
              <div className={classes.countRow}>
                <Chip label={`Rows ${total}`} className="df-chip-info" />
                {isAdmin ? <Chip label={`Crews ${crews.length}`} className="df-chip-ok" /> : null}
                {isAdmin ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={rebuilding}
                    onClick={handleRebuildFromTerritories}
                  >
                    {rebuilding ? 'Rebuilding...' : 'Rebuild From Territories'}
                  </Button>
                ) : null}
              </div>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {error ? (
        <PortalStateCard
          title="Schedule unavailable"
          message={error}
          actionLabel="Retry"
          onAction={loadSchedule}
          tone="danger"
          minHeight={240}
        />
      ) : (
        <Card className={shared.tableCard}>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={shared.tableHeadCell}>Property</TableCell>
                    <TableCell className={shared.tableHeadCell}>Due</TableCell>
                    <TableCell className={shared.tableHeadCell}>Scheduled</TableCell>
                    <TableCell className={shared.tableHeadCell}>Status</TableCell>
                    <TableCell className={shared.tableHeadCell}>Assignment</TableCell>
                    {isAdmin ? <TableCell className={shared.tableHeadCell} align="right">Actions</TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item._id} hover>
                      <TableCell>
                        <Typography variant="body2" className={shared.primaryText}>
                          {item.siteName || 'Unnamed Property'}
                        </Typography>
                        <Typography variant="body2" className={shared.secondaryText}>
                          {item.address || 'No address on file'}
                        </Typography>
                        {item.notes ? (
                          <Typography variant="body2" className={`${shared.secondaryText} ${classes.noteWrap}`}>
                            {item.notes}
                          </Typography>
                        ) : null}
                        <Box mt={1} display="flex" gridGap={8} flexWrap="wrap">
                          {item.gateCode ? <Chip size="small" label={`Gate ${item.gateCode}`} className="df-chip-info" /> : null}
                          {item.contractedForHerbicide ? <Chip size="small" label="Contracted" className="df-chip-ok" /> : null}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className={shared.primaryText}>
                          {formatPortalDate(item.effectiveNextDate, { fallback: 'Not scheduled' })}
                        </Typography>
                        <Typography variant="body2" className={shared.secondaryText}>
                          Priority: {item.priorityLabel || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className={shared.primaryText}>
                          {formatPortalDate(item.scheduledTicketDate, { fallback: 'No calendar date' })}
                        </Typography>
                        <Typography variant="body2" className={shared.secondaryText}>
                          Last applied: {formatPortalDate(item.lastAppliedDate, { fallback: 'No history' })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.statusLabel || item.status}
                          className={getStatusChipClass(item.status)}
                        />
                      </TableCell>
                      <TableCell className={classes.assignmentCell}>
                        <Typography variant="body2" className={shared.primaryText}>
                          {item.assignedCrewName || 'Unassigned'}
                        </Typography>
                        <Typography variant="body2" className={shared.secondaryText}>
                          {item.assignedTerritoryName && item.assignedTerritoryName !== 'Unassigned'
                            ? item.assignedTerritoryName
                            : 'No territory link'}
                        </Typography>
                        <Box mt={1} display="flex" gridGap={8} flexWrap="wrap">
                          <Chip
                            size="small"
                            label={item.isAssigned ? 'Assigned' : 'Unassigned'}
                            className={item.isAssigned ? 'df-chip-ok' : 'df-chip-warn'}
                          />
                          {item.assignmentSource ? (
                            <Chip size="small" label={item.assignmentSource === 'territory' ? 'Territory rule' : 'Manual'} className="df-chip-info" />
                          ) : null}
                        </Box>
                      </TableCell>
                      {isAdmin ? (
                        <TableCell align="right">
                          <div className={classes.actionRow}>
                            <TextField
                              select
                              variant="outlined"
                              size="small"
                              value={item.assignedCrew || ''}
                              disabled={assigningItemId === item._id}
                              onChange={(event) => handleAssignCrew(item, event.target.value)}
                            >
                              <MenuItem value="">Unassigned</MenuItem>
                              {crews.map((crew) => (
                                <MenuItem key={crew._id} value={crew._id}>
                                  {crew.name}
                                </MenuItem>
                              ))}
                            </TextField>
                            {item.isAssigned ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleAssignCrew(item, '')}
                                disabled={assigningItemId === item._id}
                              >
                                Clear
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                  {!items.length ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 6 : 5} className={classes.emptyCell}>
                        <PortalStateCard
                          title="No schedule items in scope"
                          message={isAdmin
                            ? 'No jobs match the current filters.'
                            : 'No jobs assigned to your crew right now.'}
                          minHeight={180}
                        />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorSchedule)
